import React, { useState, useEffect } from 'react';
import { useUserContext } from '../contexts/UserContext';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import EmptyState from '../components/ui/EmptyState';
import BranchQRModal from '../components/BranchQRModal';
import { Plus } from 'lucide-react';

interface Branch {
  id: string;
  name: string;
  displayName: string;
  qrCode?: string | null;
  addressLine?: string;
  city?: string;
  pincode?: string;
  phoneNumbers: string[];
  email?: string;
  gstin?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  gym: {
    id: string;
    name: string;
    displayName: string;
    email?: string;
    phoneNumbers: string[];
    gstin?: string;
  };
  _count: {
    userMappings: number;
    attendanceLogs: number;
  };
}



const BranchesPage: React.FC = () => {
  const { hasRole } = useUserContext();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    addressLine: '',
    city: '',
    pincode: '',
    phoneNumbers: [''],
    email: '',
    status: 'active' as 'active' | 'inactive'
  });

  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});
  const [originalFormData, setOriginalFormData] = useState<typeof formData | null>(null);

  // Validation functions
  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^(\+91)?[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validatePincode = (pincode: string): boolean => {
    const pincodeRegex = /^[1-9][0-9]{5}$/;
    return pincodeRegex.test(pincode);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (field: string, value: any): string => {
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Branch name is required';
        break;
      case 'phoneNumbers':
        const validPhones = value.filter((phone: string) => phone.trim());
        if (validPhones.length > 0) {
          const uniquePhones = [...new Set(validPhones)];
          if (uniquePhones.length !== validPhones.length) {
            return 'Duplicate phone numbers are not allowed';
          }
          for (const phone of validPhones) {
            if (!validatePhoneNumber(phone)) {
              return `Invalid phone number: ${phone}. Use 10-digit Indian mobile number.`;
            }
          }
        }
        break;
      case 'pincode':
        if (value && !validatePincode(value)) {
          return 'Invalid pincode format. Use 6-digit Indian pincode.';
        }
        break;
      case 'email':
        if (value && !validateEmail(value)) {
          return 'Invalid email format.';
        }
        break;
    }
    return '';
  };

  const validateForm = (): { isValid: boolean; errors: { [key: string]: string } } => {
    const errors: { [key: string]: string } = {};

    errors.name = validateField('name', formData.name);
    errors.phoneNumbers = validateField('phoneNumbers', formData.phoneNumbers);
    errors.pincode = validateField('pincode', formData.pincode);
    errors.email = validateField('email', formData.email);

    return {
      isValid: Object.values(errors).every(error => !error),
      errors
    };
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, [field]: true }));

    // Validate field and update errors
    const error = validateField(field, value);
    setFormErrors(prev => ({ ...prev, [field]: error }));
  };

  const handlePhoneNumberChange = (index: number, value: string) => {
    const newPhoneNumbers = [...formData.phoneNumbers];
    newPhoneNumbers[index] = value;
    setFormData(prev => ({ ...prev, phoneNumbers: newPhoneNumbers }));

    // Mark field as touched
    setTouchedFields(prev => ({ ...prev, phoneNumbers: true }));

    // Validate field and update errors
    const error = validateField('phoneNumbers', newPhoneNumbers);
    setFormErrors(prev => ({ ...prev, phoneNumbers: error }));
  };

  const handleShowQR = async (branch: Branch) => {
    // If QR code doesn't exist, try to generate it automatically
    if (!branch.qrCode) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No access token');
        }

        const apiUrl = API_BASE_URL;
        const response = await fetch(`${apiUrl}/api/branches/${branch.id}/generate-qr`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to generate QR code');
        }

        const result = await response.json();
        const updatedBranch = result.data;

        // Update local state so we don't have to fetch everything again
        setBranches(prev => prev.map(b => b.id === updatedBranch.id ? { ...b, qrCode: updatedBranch.qrCode } : b));
        setSelectedBranch({ ...branch, qrCode: updatedBranch.qrCode });
        setShowQRModal(true);
        toast.success('QR code generated successfully');
        return;
      } catch (error: any) {
        console.error('Error generating QR code on demand:', error);
        toast.error(error.message || 'Failed to generate QR code');
        return;
      }
    }

    setSelectedBranch(branch);
    setShowQRModal(true);
  };

  const isOwner = hasRole('owner');
  const isAdmin = hasRole('admin');
  const canManageBranches = isOwner || isAdmin;

  // Check if form has changes from original data
  const hasFormChanges = (): boolean => {
    if (!originalFormData) return false;

    // Compare all fields
    const fieldsMatch =
      formData.name === originalFormData.name &&
      formData.displayName === originalFormData.displayName &&
      formData.addressLine === originalFormData.addressLine &&
      formData.city === originalFormData.city &&
      formData.pincode === originalFormData.pincode &&
      formData.email === originalFormData.email &&
      formData.status === originalFormData.status;

    // Compare phone numbers arrays
    const phoneNumbersMatch =
      formData.phoneNumbers.length === originalFormData.phoneNumbers.length &&
      formData.phoneNumbers.every((phone, index) => phone === originalFormData.phoneNumbers[index]);

    return !fieldsMatch || !phoneNumbersMatch;
  };


  // Fetch branches and gyms
  const fetchData = async () => {
    try {
      setLoading(true);

      // Get user's access token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      // Use the correct API URL (port 3001)
      const apiUrl = API_BASE_URL;

      // Fetch branches
      const branchesResponse = await fetch(`${apiUrl}/api/branches`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!branchesResponse.ok) {
        if (branchesResponse.status === 404) {
          // No branches found - this is normal for new users
          setBranches([]);
        } else {
          throw new Error(`Failed to fetch branches: ${branchesResponse.status}`);
        }
      } else {
        const branchesData = await branchesResponse.json();
        setBranches(branchesData.data || []);
      }



    } catch (error) {
      console.error('Error fetching data:', error);
      // Don't show error toast for normal "no branches" case
      if (error instanceof Error && !error.message.includes('404')) {
        toast.error('Failed to load branches');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Create branch
  const handleCreateBranch = async () => {
    try {
      // Validate form
      const validation = validateForm();
      if (!validation.isValid) {
        Object.values(validation.errors).forEach((error: string) => {
          if (error) toast.error(error);
        });
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/branches`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          phoneNumbers: formData.phoneNumbers.filter(phone => phone.trim())
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create branch');
      }

      toast.success('Branch created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error: any) {
      console.error('Error creating branch:', error);
      toast.error(error.message || 'Failed to create branch');
    }
  };

  // Update branch
  const handleUpdateBranch = async () => {
    if (!selectedBranch) return;

    try {
      // Validate form
      const validation = validateForm();
      if (!validation.isValid) {
        Object.values(validation.errors).forEach((error: string) => {
          if (error) toast.error(error);
        });
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/branches/${selectedBranch.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          phoneNumbers: formData.phoneNumbers.filter(phone => phone.trim())
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update branch');
      }

      toast.success('Branch updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchData();
    } catch (error: any) {
      console.error('Error updating branch:', error);
      toast.error(error.message || 'Failed to update branch');
    }
  };

  // Delete branch
  const handleDeleteBranch = async () => {
    if (!selectedBranch) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/branches/${selectedBranch.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete branch');
      }

      toast.success('Branch deleted successfully');
      setShowDeleteModal(false);
      setSelectedBranch(null);
      fetchData();
    } catch (error: any) {
      console.error('Error deleting branch:', error);
      toast.error(error.message || 'Failed to delete branch');
    }
  };

  // Toggle branch status
  const handleToggleStatus = async (branch: Branch) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const newStatus = branch.status === 'active' ? 'inactive' : 'active';
      const apiUrl = API_BASE_URL;

      const response = await fetch(`${apiUrl}/api/branches/${branch.id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update branch status');
      }

      toast.success(`Branch ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      fetchData();
    } catch (error: any) {
      console.error('Error updating branch status:', error);
      toast.error(error.message || 'Failed to update branch status');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      addressLine: '',
      city: '',
      pincode: '',
      phoneNumbers: [''],
      email: '',
      status: 'active'
    });
    setFormErrors({});
    setTouchedFields({});
    setOriginalFormData(null); // Clear original data
  };

  // Open edit modal
  const openEditModal = (branch: Branch) => {
    setSelectedBranch(branch);
    const initialFormData = {
      name: branch.name,
      displayName: branch.displayName,
      addressLine: branch.addressLine || '',
      city: branch.city || '',
      pincode: branch.pincode || '',
      phoneNumbers: branch.phoneNumbers.length > 0 ? branch.phoneNumbers : [''],
      email: branch.email || '',
      status: branch.status
    };
    setFormData(initialFormData);
    setOriginalFormData(initialFormData); // Save original data for comparison
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (branch: Branch) => {
    setSelectedBranch(branch);
    setShowDeleteModal(true);
  };

  // Add phone number field
  const addPhoneNumber = () => {
    setFormData(prev => ({
      ...prev,
      phoneNumbers: [...prev.phoneNumbers, '']
    }));
  };

  // Remove phone number field
  const removePhoneNumber = (index: number) => {
    setFormData(prev => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Premium Header */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-full blur-3xl -mr-32 -mt-32 transition-colors group-hover:bg-blue-100/50"></div>
        <div className="relative">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">Gym Branches</h1>
          <p className="text-[10px] font-black text-gray-400 mt-2 uppercase tracking-[0.2em]">Locations & Access Points</p>
        </div>

        {canManageBranches && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-blue-100 flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Branch</span>
          </button>
        )}
      </div>

      {/* Branches List */}
      {branches.length === 0 ? (
        <EmptyState
          icon="🏋️"
          title="No branches yet"
          description={canManageBranches ? "Create your first branch to get started" : "No branches available"}
          action={canManageBranches ? (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add Branch</span>
            </button>
          ) : undefined}
        />
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {branches.map((branch) => (
            <div key={branch.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group">
              {/* Header with gradient background */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 text-white">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-1">{branch.displayName}</h3>
                    <p className="text-blue-100 text-sm opacity-90">{branch.name}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-blue-100 opacity-75">Status</span>
                    <button
                      onClick={() => handleToggleStatus(branch)}
                      className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors ${branch.status === 'active' ? 'bg-green-400' : 'bg-gray-400'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${branch.status === 'active' ? 'translate-x-5' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="space-y-3 mb-6">
                  {branch.addressLine && (
                    <div className="flex items-start group/item">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <span className="text-sm text-gray-700 leading-relaxed">{branch.addressLine}</span>
                    </div>
                  )}
                  {(branch.city || branch.pincode) && (
                    <div className="flex items-start group/item">
                      <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <span className="text-sm text-gray-700 leading-relaxed">
                        {[branch.city, branch.pincode].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {branch.phoneNumbers.length > 0 && (
                    <div className="flex items-start group/item">
                      <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div className="text-sm text-gray-700 leading-relaxed">
                        {branch.phoneNumbers.map((phone, index) => (
                          <div key={index} className="hover:text-purple-600 transition-colors">{phone}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  {branch.email && (
                    <div className="flex items-start group/item">
                      <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-sm text-gray-700 leading-relaxed hover:text-orange-600 transition-colors">{branch.email}</span>
                    </div>
                  )}
                  {branch.gstin && (
                    <div className="flex items-start group/item">
                      <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <span className="text-sm text-gray-700 leading-relaxed hover:text-indigo-600 transition-colors">{branch.gstin}</span>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                {canManageBranches && (
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => handleShowQR(branch)}
                      className="px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all duration-200 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                      <span>QR Code</span>
                    </button>
                    <button
                      onClick={() => openEditModal(branch)}
                      className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => openDeleteModal(branch)}
                      className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Branch Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Create New Branch</h2>
              <form onSubmit={(e) => { e.preventDefault(); handleCreateBranch(); }}>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Branch Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      onBlur={() => setTouchedFields(prev => ({ ...prev, name: true }))}
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${touchedFields.name && formErrors.name
                        ? 'border-red-300 focus:ring-red-500 bg-red-50'
                        : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      placeholder="Enter branch name"
                      required
                    />
                    {touchedFields.name && formErrors.name && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Display Name</label>
                    <input
                      type="text"
                      value={formData.displayName}
                      onChange={(e) => handleFieldChange('displayName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter display name (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                    <input
                      type="text"
                      value={formData.addressLine}
                      onChange={(e) => handleFieldChange('addressLine', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter street address"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleFieldChange('city', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="Enter city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Pincode</label>
                      <input
                        type="text"
                        value={formData.pincode}
                        onChange={(e) => handleFieldChange('pincode', e.target.value)}
                        onBlur={() => setTouchedFields(prev => ({ ...prev, pincode: true }))}
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${touchedFields.pincode && formErrors.pincode
                          ? 'border-red-300 focus:ring-red-500 bg-red-50'
                          : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'
                          }`}
                        placeholder="6-digit pincode"
                        maxLength={6}
                      />
                      {touchedFields.pincode && formErrors.pincode && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <span className="mr-1">⚠️</span>
                          {formErrors.pincode}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Numbers</label>
                    {formData.phoneNumbers.map((phone, index) => (
                      <div key={index} className="flex gap-3 mb-3">
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => handlePhoneNumberChange(index, e.target.value)}
                          onBlur={() => setTouchedFields(prev => ({ ...prev, phoneNumbers: true }))}
                          className={`flex-1 px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${touchedFields.phoneNumbers && formErrors.phoneNumbers
                            ? 'border-red-300 focus:ring-red-500 bg-red-50'
                            : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                          placeholder="10-digit mobile number"
                          maxLength={13}
                        />
                        {formData.phoneNumbers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePhoneNumber(index)}
                            className="px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addPhoneNumber}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add another phone number
                    </button>
                    {touchedFields.phoneNumbers && formErrors.phoneNumbers && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {formErrors.phoneNumbers}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleFieldChange('email', e.target.value)}
                      onBlur={() => setTouchedFields(prev => ({ ...prev, email: true }))}
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${touchedFields.email && formErrors.email
                        ? 'border-red-300 focus:ring-red-500 bg-red-50'
                        : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      placeholder="Enter email address"
                    />
                    {touchedFields.email && formErrors.email && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-600">Inactive</span>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          status: prev.status === 'active' ? 'inactive' : 'active'
                        }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.status === 'active' ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${formData.status === 'active' ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                      </button>
                      <span className="text-sm text-gray-600">Active</span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Create Branch
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Branch Modal */}
      {showEditModal && selectedBranch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Edit Branch</h2>
              <form onSubmit={(e) => { e.preventDefault(); handleUpdateBranch(); }}>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Branch Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      onBlur={() => setTouchedFields(prev => ({ ...prev, name: true }))}
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${touchedFields.name && formErrors.name
                        ? 'border-red-300 focus:ring-red-500 bg-red-50'
                        : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      placeholder="Enter branch name"
                      required
                    />
                    {touchedFields.name && formErrors.name && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Display Name</label>
                    <input
                      type="text"
                      value={formData.displayName}
                      onChange={(e) => handleFieldChange('displayName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter display name (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
                    <input
                      type="text"
                      value={formData.addressLine}
                      onChange={(e) => handleFieldChange('addressLine', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Enter street address"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleFieldChange('city', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="Enter city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Pincode</label>
                      <input
                        type="text"
                        value={formData.pincode}
                        onChange={(e) => handleFieldChange('pincode', e.target.value)}
                        onBlur={() => setTouchedFields(prev => ({ ...prev, pincode: true }))}
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${touchedFields.pincode && formErrors.pincode
                          ? 'border-red-300 focus:ring-red-500 bg-red-50'
                          : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'
                          }`}
                        placeholder="6-digit pincode"
                        maxLength={6}
                      />
                      {touchedFields.pincode && formErrors.pincode && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <span className="mr-1">⚠️</span>
                          {formErrors.pincode}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Numbers</label>
                    {formData.phoneNumbers.map((phone, index) => (
                      <div key={index} className="flex gap-3 mb-3">
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => handlePhoneNumberChange(index, e.target.value)}
                          onBlur={() => setTouchedFields(prev => ({ ...prev, phoneNumbers: true }))}
                          className={`flex-1 px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${touchedFields.phoneNumbers && formErrors.phoneNumbers
                            ? 'border-red-300 focus:ring-red-500 bg-red-50'
                            : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                          placeholder="10-digit mobile number"
                          maxLength={13}
                        />
                        {formData.phoneNumbers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePhoneNumber(index)}
                            className="px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addPhoneNumber}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add another phone number
                    </button>
                    {touchedFields.phoneNumbers && formErrors.phoneNumbers && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {formErrors.phoneNumbers}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleFieldChange('email', e.target.value)}
                      onBlur={() => setTouchedFields(prev => ({ ...prev, email: true }))}
                      className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${touchedFields.email && formErrors.email
                        ? 'border-red-300 focus:ring-red-500 bg-red-50'
                        : 'border-gray-200 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                      placeholder="Enter email address"
                    />
                    {touchedFields.email && formErrors.email && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <span className="mr-1">⚠️</span>
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-600">Inactive</span>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          status: prev.status === 'active' ? 'inactive' : 'active'
                        }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${formData.status === 'active' ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${formData.status === 'active' ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                      </button>
                      <span className="text-sm text-gray-600">Active</span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowEditModal(false); resetForm(); }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!hasFormChanges()}
                    className={`px-4 py-2 rounded-md transition-colors ${hasFormChanges()
                      ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                  >
                    Update Branch
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Branch Modal */}
      {showDeleteModal && selectedBranch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Delete Branch</h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{selectedBranch.displayName}</strong>?
            </p>
            <p className="text-sm text-gray-500 mb-4">
              This action cannot be undone. The branch will be permanently removed.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteBranch}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete Branch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && selectedBranch && (
        <BranchQRModal
          branch={selectedBranch}
          onClose={() => {
            setShowQRModal(false);
            setSelectedBranch(null);
          }}
        />
      )}
    </div>
  );
};

export default BranchesPage; 